import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useThreads, useMessages, useMarkThreadRead, queryKeys } from "../lib/queries";
import { getCurrentCognitoSub, isCoach } from "../lib/auth";
import { ThreadMessage } from "../lib/websocket";
import { createMessagingConnection, MessagingConnection, ConnectionStatus } from "../lib/messagingConnection";
import { LoadingState, ErrorState } from "../components/Loading";
import { PageShell } from "../components/PageShell";

interface ThreadInfo {
  id: string;
  thread_type: "private" | "group";
  /**
   * Who the conversation is actually with, in real names, computed server-side for this viewer.
   *
   * The chooser used to read "Guardian only" and "Everyone", which names the data model rather than the
   * room: a guardian could not tell from it whether their child was reading along. Server-side because
   * only the server knows the other participants' names, and because the answer depends on who asks --
   * the same thread is "Private chat with Dana Reyes" to the coach and "Private chat with Coach Sam" to
   * the guardian.
   */
  label: string;
  /** The private thread is one conversation covering every athlete this guardian has. */
  shared_across_clients: boolean;
  /** Role -> person, so a message is attributed to a name rather than to a job title. */
  participant_names: { coach: string; guardian: string; athlete: string };
}

/**
 * Who is in the room, in names rather than in thread types.
 *
 * Spelled out per thread because the difference is safeguarding-relevant: the group thread includes the
 * athlete, who may be a child, and the private one does not. A parent about to write something about
 * their child needs to know which of those they are looking at without inferring it from a tab label.
 */
function describeAudience(thread: ThreadInfo): string {
  const { coach, athlete } = thread.participant_names;
  if (thread.thread_type === "group") {
    return `${coach} and ${athlete} can both read this.`;
  }
  const scope = thread.shared_across_clients
    ? " It covers every athlete in your family, not just one."
    : "";
  return `Just you and ${coach} — ${athlete} can't see this.${scope}`;
}

/**
 * When a message was sent, at the resolution that is actually useful.
 *
 * Messages carried no time at all: a parent who sent "Chloe's knee hurt, should she skip the jumps?" at
 * 10pm had no way to tell later how long ago that was, and a coach triaging between sessions could not
 * tell an hour-old message from a week-old one (UX review F75). Same-day messages get a clock time,
 * because "was that before or after training" is the question; older ones get a date.
 */
export function formatSentAt(iso: string, now = Date.now()): string {
  const sent = new Date(iso);
  if (Number.isNaN(sent.getTime())) return "";
  const sameDay = new Date(now).toDateString() === sent.toDateString();
  return sameDay
    ? sent.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })
    : sent.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/**
 * Shared by both the coach-facing and family-facing routes (PRD §8.1-8.2) -- the UI logic is identical
 * either way; which thread types even exist is entirely driven by the backend's response, not something
 * this component decides. A minor client with a guardian relationship gets a private/group chooser; an
 * adult self-managed client gets exactly one thread and renders with NO chooser at all -- this is the
 * named prototype bug ("a chooser must never render for an adult-only relationship"), and here that's
 * true by construction: `threads.length` is just however many the backend actually returned.
 *
 * `backTo` is an explicit absolute path, not a relative `to=".."`. The routes are flat, so a relative
 * parent link resolved all the way to "/" -- the login page -- which is exactly what made "Back" look
 * like it was signing the user out.
 */
export function Messages({ backTo }: { backTo: string }) {
  const { clientId } = useParams<{ clientId: string }>();
  const queryClient = useQueryClient();

  const threadsQuery = useThreads<ThreadInfo[]>(clientId);
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [mySub, setMySub] = useState<string | null>(null);
  const [myRole, setMyRole] = useState<ThreadMessage["sender_role"]>("guardian");
  const [sendError, setSendError] = useState<string | null>(null);

  const threads = threadsQuery.data;
  const resolvedThreadId = activeThreadId ?? threads?.[0]?.id ?? null;
  const messagesQuery = useMessages(resolvedThreadId);

  const connectionRef = useRef<MessagingConnection | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>("connecting");
  const activeThreadIdRef = useRef<string | null>(null);
  activeThreadIdRef.current = resolvedThreadId;

  useEffect(() => {
    void getCurrentCognitoSub().then(setMySub);
    // The optimistic append needs a role. "guardian" is the safe default of the two family roles: an
    // athlete seeing their own message is rendered as "You" anyway, so the fallback is only ever visible
    // in the window before this resolves.
    void isCoach().then((coach) => setMyRole(coach ? "coach" : "guardian"));
  }, []);

  useEffect(() => {
    const connection = createMessagingConnection({
      onMessage: (message) => {
        // Push straight into the cache for the thread it belongs to, so a message that arrives for a
        // thread the user isn't currently looking at is still there when they switch to it -- the old
        // version dropped those on the floor unless the thread happened to be open.
        queryClient.setQueryData<ThreadMessage[]>(queryKeys.messages(message.thread_id), (prev) =>
          prev ? [...prev, message] : prev
        );
      },
      onStatusChange: setConnectionStatus,
      // Anything sent while the socket was down was never pushed here and the server doesn't replay it,
      // so without this a reconnect would look healthy while silently missing the whole gap.
      onReconnected: () => {
        void queryClient.invalidateQueries({ queryKey: ["messages"] });
      },
    });
    connectionRef.current = connection;
    return () => {
      connection.close();
      connectionRef.current = null;
    };
  }, [queryClient]);

  /**
   * Reading is what clears the dot, so it is marked from the page that shows the messages -- once per
   * thread opened, and again when a new message lands while you are looking at it. The mutation is
   * fire-and-forget: a failed mark leaves the dot up, which is the harmless direction to fail in.
   */
  const markRead = useMarkThreadRead();
  const markReadRef = useRef(markRead);
  markReadRef.current = markRead;
  const lastMessageId = messagesQuery.data?.[messagesQuery.data.length - 1]?.id ?? null;
  useEffect(() => {
    if (!resolvedThreadId) return;
    markReadRef.current.mutate(resolvedThreadId);
  }, [resolvedThreadId, lastMessageId]);

  /**
   * Takes anything that can be prevented, so the composer's Enter key and the form's submit can share
   * one path rather than duplicating the send logic (F77).
   */
  function handleSend(event: { preventDefault: () => void }) {
    event.preventDefault();
    if (!resolvedThreadId || !draft.trim()) return;

    // `send` reports whether it actually went out. It used to say "Still connecting — try again in a
    // moment", which was untrue once the socket had dropped: nothing was reconnecting, so waiting
    // achieved nothing and only a page reload recovered. Now it is true, because a reconnect really is
    // in flight -- and the draft is deliberately left in the box so the message isn't lost.
    if (!connectionRef.current?.send(resolvedThreadId, draft)) {
      setSendError("Reconnecting — your message hasn't been sent yet. Try again in a moment.");
      return;
    }

    setSendError(null);
    // The sender never receives their own push (lambda/messages/index.ts's getOtherParticipantCognitoSubs
    // deliberately excludes the sender), so this appends locally rather than waiting on a round-trip.
    queryClient.setQueryData<ThreadMessage[]>(queryKeys.messages(resolvedThreadId), (prev) => [
      ...(prev ?? []),
      {
        id: `optimistic-${Date.now()}`,
        thread_id: resolvedThreadId,
        sender_cognito_sub: mySub ?? "",
        // Was hardcoded "coach". Mostly masked, because a row whose sub matches yours renders as "You"
        // -- but if the sub lookup hadn't resolved yet the row rendered as somebody else's and a
        // guardian saw their own message attributed to the coach.
        sender_role: myRole,
        body: draft,
        created_at: new Date().toISOString(),
      },
    ]);
    setDraft("");
  }

  const messages = messagesQuery.data ?? [];
  const activeThread = threads?.find((thread) => thread.id === resolvedThreadId) ?? null;

  return (
    <PageShell
      title="Messages"
      actions={
        <Link className="link-button" to={backTo}>
          Back
        </Link>
      }
    >
      {/* Previously invisible: the socket dropped after ten minutes idle and the page carried on looking
          fine while receiving nothing. Announced politely rather than as an alert -- it recovers on its
          own, so it shouldn't interrupt a screen reader mid-sentence. */}
      {connectionStatus === "reconnecting" && (
        <p className="badge badge-warn" role="status" aria-live="polite">
          Reconnecting…
        </p>
      )}

      {sendError && (
        <p role="alert" className="error">
          {sendError}
        </p>
      )}

      {threadsQuery.isPending && <LoadingState label="Loading conversation…" />}
      {threadsQuery.isError && <ErrorState error={threadsQuery.error} onRetry={() => threadsQuery.refetch()} />}

      {threads && threads.length > 1 && (
        <div className="filter-chips">
          {threads.map((thread) => (
            <button
              key={thread.id}
              type="button"
              className={`chip ${thread.id === resolvedThreadId ? "active" : ""}`}
              onClick={() => setActiveThreadId(thread.id)}
            >
              {thread.label}
            </button>
          ))}
        </div>
      )}

      {/* Who can read this, per thread, changing with the tab.
          One fixed line used to sit under both tabs -- "This conversation covers all of this family's
          athletes" -- so it appeared to describe whichever tab was showing, and selecting the group
          thread gave no explanation of who could read it at all. That distinction is
          safeguarding-relevant: one of these threads includes an eleven-year-old, and it is where a
          parent decides whether to type "Chloe's knee hurt" (UX review F76). */}
      {activeThread && <p className="form-hint">{describeAudience(activeThread)}</p>}

      {messagesQuery.isPending && resolvedThreadId && <LoadingState label="Loading messages…" />}
      {messagesQuery.isError && <ErrorState error={messagesQuery.error} onRetry={() => messagesQuery.refetch()} />}

      {messagesQuery.isSuccess &&
        (messages.length === 0 ? (
          <p className="empty-state">No messages yet.</p>
        ) : (
          <ul className="message-thread">
            {messages.map((message) => {
              const mine = message.sender_cognito_sub === mySub;
              return (
                <li key={message.id} className={`message-bubble-row ${mine ? "mine" : ""}`}>
                  <div className="message-bubble">
                    {/* A name, not a role. "guardian" is what the database calls the person a child
                        calls Mum, and rendering the column value was never right. */}
                    <div className="message-sender">
                      <span>
                        {mine ? "You" : (activeThread?.participant_names[message.sender_role] ?? message.sender_role)}
                      </span>
                      {/* The <time> element carries the full timestamp so hovering or a screen reader
                          gives the exact moment, while the visible text stays short (F75). */}
                      {message.created_at && (
                        <time className="message-time" dateTime={message.created_at}>
                          {formatSentAt(message.created_at)}
                        </time>
                      )}
                    </div>
                    {/* Coerced, not rendered raw. The server now rejects a non-string body, but a row
                        stored before that fix (or by anything else that ever writes one) rendering as a
                        bare object throws React error #31 and replaces the WHOLE page with the error
                        boundary -- for every participant, unrecoverable by reload. One bad row should
                        look wrong, not take the conversation down. */}
                    <div className="message-body">{String(message.body ?? "")}</div>
                  </div>
                </li>
              );
            })}
          </ul>
        ))}

      {/* A textarea that grows, not a one-line input.
          Typing two sentences pushed the beginning off the left edge, so you could only ever see the
          tail of what you were writing and could not review it before sending -- worst for a coach
          replying rinkside on a phone (UX review F77). Enter still sends, because that is what everyone
          expects of a message box; Shift+Enter takes a new line. */}
      <form className="message-composer" onSubmit={handleSend}>
        <textarea
          aria-label="Message"
          value={draft}
          rows={Math.min(6, draft.split("\n").length + 1)}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSend(e);
            }
          }}
          placeholder="Write a message…"
        />
        <button type="submit" disabled={!draft.trim() || !resolvedThreadId}>
          Send
        </button>
      </form>
    </PageShell>
  );
}
